/* eslint-disable sort-keys-fix/sort-keys-fix */
import {
  Body1,
  ColorClass,
  FlexBox,
  FontClass,
  GlobalClass,
  TextButton,
  TextButtonTheme,
  WrapWithTooltip,
} from "ui";
import { CameraData } from "@web/types/CameraData";
import { CameraDataForDisplay } from "@web/types/CameraDataForDisplay";
import { Maybe, filterNulls } from "shared";

const NOT_APPLICABLE = "N/A";

function getShutterSpeed(cameraData: CameraData) {
  const shutterSpeed = `Shutter speed from ${cameraData["Fastest Shutter Speed (seconds)"]} to ${cameraData["Slowest Shutter Speed (seconds)"]} seconds`;
  if (cameraData["Manual Shutter Speed (seconds)"] === "N/A") {
    return <Body1 colorClass={ColorClass.Primary}>{shutterSpeed}</Body1>;
  }

  return (
    <Body1 colorClass={ColorClass.Primary}>
      {shutterSpeed} (
      <WrapWithTooltip tooltipContent="The shutter speed used when there is no battery power.">
        <div
          className={GlobalClass.TooltipUnderline}
          style={{ display: "inline" }}
        >
          mechanical fallback
        </div>
      </WrapWithTooltip>{" "}
      of {cameraData["Manual Shutter Speed (seconds)"]})
    </Body1>
  );
}

function getFalseColumn(_propertyName: string) {
  return "No";
}

function getTrueColumn(_propertyName: string) {
  return "Yes";
}

function createLabelValuePair<T>(label: string, value: T) {
  return { label, value };
}

// function to handle conditional properties
function conditionalProp(
  label: string,
  propName: string,
  cameraData: Record<string, any>
) {
  return createLabelValuePair(
    label,
    cameraData[propName] ? getTrueColumn(propName) : getFalseColumn(propName)
  );
}

export function getCameraDataForDisplay(
  cameraData: CameraData,
  nullValue: Maybe<string> = null
): CameraDataForDisplay {
  const tooltipContent = (
    <Body1 colorClass={ColorClass.Primary} style={{ maxWidth: 300 }}>
      A fully mechanical camera has no electronics in the shutter, exposure or
      film-winding mechanisms. It may have a light meter, but mechanically
      speaking, it&apos;s fully functional without a battery.
      <br />
      <br />
      Electric cameras, on the other hand, typically have limited functionality
      (e.g. only one usable shutter speed) without a battery.
    </Body1>
  );

  const mechanicalOrElectric = (
    <WrapWithTooltip tooltipContent={tooltipContent}>
      {/* So tooltip positioning is correct */}
      <div style={{ display: "inline-block" }}>
        <Body1
          className={GlobalClass.TooltipUnderline}
          colorClass={ColorClass.Primary}
        >
          {cameraData["Fully Mechanical or Electric"]}
        </Body1>
      </div>
    </WrapWithTooltip>
  );

  const additionalInfoObject = {
    Viewfinder: createLabelValuePair("Viewfinder", cameraData.Viewfinder),
    "Focusing Screen": createLabelValuePair(
      "Focusing Screen",
      cameraData["Focusing Screen"]
    ),
    "Fastest Flash Sync Speed (seconds)": createLabelValuePair(
      "Fastest Flash Sync Speed (seconds)",
      `Fastest flash sync speed: ${cameraData["Fastest Flash Sync Speed (seconds)"]}`
    ),
    "Lenses Supported": createLabelValuePair(
      "Lenses Supported",
      cameraData["Lenses Supported"]
    ),
  };

  return {
    Description: createLabelValuePair("Description", cameraData.Description),
    Market: createLabelValuePair(
      "Target Audience",
      `Targeted towards ${cameraData.Market.toLowerCase()} consumers`
    ),
    "Fully Mechanical or Electric": createLabelValuePair(
      "Shutter Type",
      mechanicalOrElectric
    ),
    "Shutter Speed": createLabelValuePair(
      "Shutter Speed",
      getShutterSpeed(cameraData)
    ),
    "Aperture Priority Mode": conditionalProp(
      "Aperture Priority Mode",
      "Aperture Priority Mode",
      cameraData
    ),
    Weight: createLabelValuePair("Weight", `Body weighs ${cameraData.Weight}`),
    ASA: createLabelValuePair(
      "ASA",
      cameraData["ASA Min"] === NOT_APPLICABLE
        ? "N/A, camera does not have built-in light meter, and does not need to know the ISO of the film"
        : `ASA from ${cameraData["ASA Min"]} to ${cameraData["ASA Max"]}`
    ),
    Battery: createLabelValuePair("Battery", cameraData.Battery),
    "Bulb Mode": conditionalProp("Bulb Mode", "Bulb Mode", cameraData),
    "Average Price": createLabelValuePair(
      "Average Price",
      `Average price on Ebay: ${cameraData["Average Price"]}`
    ),
    "Advanced Features": createLabelValuePair(
      "Advanced Features",
      filterNulls([
        cameraData["Depth of Field Preview"] ? "Depth of Field Preview" : null,
        cameraData["Memory Lock"] ? "Memory Lock" : null,
        cameraData["Program Mode"] ? "Program Mode" : null,
        cameraData["Timer"] ? "Timer" : null,
      ]).join(", ")
    ),
    "Common Issues": createLabelValuePair(
      "Common Issues",
      cameraData["Common Issues"] === "" ? (
        nullValue
      ) : (
        <Body1 colorClass={ColorClass.Primary}>
          <div
            dangerouslySetInnerHTML={{ __html: cameraData["Common Issues"] }}
          />
        </Body1>
      )
    ),
    "Additional Information": createLabelValuePair(
      "Additional Information",
      <FlexBox flexDirection="column" gap={12}>
        <Body1 colorClass={ColorClass.Primary}>
          {additionalInfoObject["Viewfinder"].value}
        </Body1>
        <Body1 colorClass={ColorClass.Primary}>
          {additionalInfoObject["Focusing Screen"].value}
        </Body1>
        <Body1 colorClass={ColorClass.Primary}>
          {additionalInfoObject["Fastest Flash Sync Speed (seconds)"].value}
        </Body1>
        <Body1 colorClass={ColorClass.Primary}>
          {additionalInfoObject["Lenses Supported"].value}
        </Body1>
      </FlexBox>
    ),
    "Link to Manual": createLabelValuePair(
      "Link to Manual",
      <TextButton
        buttonThemeOrColorClass={TextButtonTheme.Primary}
        fontClass={FontClass.Body1}
        href={cameraData["Link to Manual"]}
        type="link_external"
      >
        Link to {cameraData["Camera Name"]} manual
      </TextButton>
    ),
  };
}
